import { persistentUid } from './uid.js'
import {
  asConversationId,
  type ClientFrame, type ConversationId,
} from './protocol/index.js'
import { ChatStore } from './store.js'
import { ConnectionManager } from './connection.js'
import { PersistentOutbox } from './outbox.js'
import { E2ESession, extractX3DHInit } from './e2e.js'
import { Renderer, type WidgetConfig } from './renderer.js'
import { restoreHistory, resolveRelayUrls } from './history.js'
import { mountChatList, type ChatListEntry, type ChatListHandle } from './chatlist.js'
import { resolveStrings, resolveLocale, isRTL, type WidgetI18nOverrides } from './i18n.js'

export interface UserInfo {
  /** Display name shown in the conversation (e.g. "Sarah Chen"). */
  name?:    string
  /** Email address — passed as conversation metadata for agent context. */
  email?:   string
  /** Avatar URL — shown as the guest's avatar in both widget and dashboard. */
  avatar?:  string
  /** Any custom key/value metadata to attach to the conversation
   *  (e.g. plan tier, account ID, page URL). Shown to agents in the sidebar. */
  meta?:    Record<string, string>
}

export interface MountOptions {
  el:           HTMLElement
  /** Relay URL. Any scheme works — `https://api.example.com` is fine; the widget
   *  derives the WebSocket URL (`wss://…/ws`) and REST base from it. */
  url:          string
  /** HTTP(S) base for REST calls — only needed when the REST API is on a
   *  DIFFERENT origin than the socket. Normally leave unset. */
  apiUrl?:      string
  profileId:    string
  subjectId?:   string
  /** Open a user↔user direct conversation with `peerId` instead of a support
   *  thread. Requires signed identity on the chatroom (both `kind: 'direct'`
   *  and `peerId` together; `subjectId` is ignored — the server derives the
   *  symmetric DM key so both sides land in the SAME conversation). */
  kind?:        'direct'
  peerId?:      string
  /** IDENTITY (tiered — the host owns identity, the widget never has to persist it):
   *  1. `token` — a signed identity token. Either a capability token, or (recommended
   *     for embedders) an ES256 JWT `{sub,iat,exp}` signed by your backend with the
   *     private key whose public half is set as the chatroom's `guestPublicKey`.
   *     The server cryptographically verifies it. Works in ANY language/environment,
   *     no cookies or storage required. This is the production path.
   *  2. `userId` — a stable id you already have for the visitor (e.g. your logged-in
   *     user id). Unauthenticated ("you vouch for it") but works everywhere. Used
   *     only when `token` is absent.
   *  3. Neither — the widget falls back to best-effort local identity on the host
   *     origin (first-party cookie + localStorage). A returning visitor on the same
   *     browser keeps their history; if storage is blocked they get a fresh chat. */
  token?:       string
  /** Called when a signed token is rejected (expired): return a fresh token
   *  from your backend to renew the session without a reload. */
  refreshToken?: () => Promise<string | null>
  userId?:      string
  subject?:     WidgetConfig['subject']
  quickReplies?: string[]
  accent?:      string
  /** Secondary accent (guest bubble + send button). Defaults to the design
   *  blue; omit and it follows `accent` for a cohesive single-token retheme. */
  accent2?:     string
  /** Colour scheme. Default 'light'. 'auto' follows the OS; 'dark'/'light' force it. */
  theme?:       'auto' | 'light' | 'dark'
  /** Load Baloo 2 + Nunito webfonts (default true). false = system fonts only. */
  webfont?:     boolean
  /** Stack navigation: when set, the chatroom header shows a back chevron that
   *  calls this. Used by `<ChatApp>` so tapping a conversation opens the room and
   *  the back arrow returns to the list — native-app style. */
  onBack?:      () => void
  /** Turn this into a self-contained inbox: the back chevron opens the user's
   *  full thread list IN PLACE (no navigation), and picking a row opens that
   *  thread with a back chevron returning to the list. */
  inbox?:       boolean
  /** Inbox scope: 'tenant' (default) lists the user's threads across ALL your
   *  chatrooms; 'profile' limits to this chatroom. */
  inboxScope?:  'tenant' | 'profile'
  /** Target language for the 🌐 translate button shown under every incoming
   *  message (via the server's /translate endpoint). Defaults to the guest's
   *  own language (from `locale`/navigator.language) — translation is ON by
   *  default. Set a specific ISO code to force it, or '' to disable. */
  translateLang?: string
  /** If true, mount as a floating launcher button that opens/closes the chat */
  launcher?:    boolean
  /** Position of the launcher button: default 'bottom-right' */
  position?:    'bottom-right' | 'bottom-left'
  /** Launcher teaser — the "optional message" card shown ABOVE the closed
   *  launcher button to invite a chat (like Channel.io's greeting). Pass a
   *  string for just a title, or `{ title, subtitle }`. If omitted, the
   *  widget uses `defaults.launcherMessage` from the chatroom manifest when
   *  present. Dismissible by the visitor (remembered for the browser session);
   *  auto-hides once the chat is opened. Only applies in `launcher` mode. */
  launcherMessage?: string | { title: string; subtitle?: string }
  /** Optional user info for identified users. When provided, the name/email/
   *  avatar are shown to agents in the dashboard instead of the anonymous ID.
   *  The token still controls identity — this is display metadata only.
   *  Anonymous users (no token, no user) remain fully anonymous. */
  user?:        UserInfo
  /** Optional per-tenant feature switches. All default to ON (omit for current
   *  behavior). Set a flag to `false` to disable the feature — the widget then
   *  does no work for it (no reaction picker built per message, no CSAT panel,
   *  no KB-deflection lookups). Gating is by not wiring the handler, so the
   *  renderer skips the feature entirely. */
  features?: {
    reactions?:  boolean   // emoji reactions on messages (default true)
    csat?:       boolean   // post-resolution satisfaction survey (default true)
    deflection?: boolean   // pre-first-message KB article suggestions (default true)
  }
  /** UI language for built-in strings. A BCP-47 tag ('ko', 'ko-KR'), bare code,
   *  or name. Omit to auto-detect from the visitor's browser (navigator.language).
   *  Currently Korean ⇄ English; anything non-Korean resolves to English. */
  locale?: string
  /** i18n: override UI strings. All keys are optional — omitted keys use the
   *  built-in dictionary for the resolved `locale` (English by default). */
  i18n?: WidgetI18nOverrides
}

export interface WidgetHandle { close(): void }


// ── Mount registry ────────────────────────────────────────────────────────────
// Tracks active widget instances per host element. Prevents double-mounting
// when React strict mode, HMR, or caller code calls mount() twice on the same
// element — the most common cause of two widgets appearing on one page.
const _registry = new WeakMap<Element, WidgetHandle>()
// Launcher widgets attach to document.body (not the ref div), and in launcher
// mode React may re-create the ref div on re-render — so the el-keyed registry
// above can't catch a stale launcher. This slot-keyed registry guarantees at
// most ONE launcher per (profileId, subjectId), so an identity flicker or
// re-render can never leave two stacked bubbles/panels on the page.
const _launcherRegistry = new Map<string, WidgetHandle>()
function launcherSlot(opts: MountOptions): string {
  return `relay-launcher::${opts.profileId}::${opts.subjectId ?? ''}`
}

// One AudioContext for ALL widget instances on the page (notification blips
// are fire-and-forget; nothing about them is per-instance).
let _audioCtx: AudioContext | null = null
function getAudioContext(): AudioContext | null {
  if (_audioCtx && _audioCtx.state !== 'closed') return _audioCtx
  try { _audioCtx = new AudioContext(); return _audioCtx } catch { return null }
}

/** Unmount any widget currently mounted on `el`. No-op if nothing is mounted. */
export function unmount(el: Element): void {
  _registry.get(el)?.close()
  _registry.delete(el)
}

// Vanilla in-widget inbox: a thread list ⇄ single thread, swapped in place inside
// the host element. Reuses mountChatList + the single-thread mount() (same pieces
// the React ChatApp composes) so the HTML embed gets a working inbox with no
// page navigation — the back chevron in a thread returns to the list.
function mountInbox(opts: MountOptions): WidgetHandle {
  const el = opts.el
  let room: WidgetHandle | null = null
  let list: ChatListHandle | null = null
  let closed = false

  const clear = () => { room?.close(); room = null; list?.close(); list = null; el.innerHTML = '' }

  function openList() {
    if (closed) return
    clear()
    list = mountChatList({
      el, url: opts.url,
      ...(opts.apiUrl ? { apiUrl: opts.apiUrl } : {}),
      ...(opts.profileId ? { profileId: opts.profileId } : {}),
      scope: opts.inboxScope ?? 'tenant',
      ...(opts.token ? { token: opts.token } : {}),
      ...(opts.userId ? { userId: opts.userId } : {}),
      ...(opts.accent ? { accent: opts.accent } : {}),
      ...(opts.theme ? { theme: opts.theme } : {}),
      ...(opts.webfont === false ? { webfont: false } : {}),
      ...(opts.locale ? { locale: opts.locale } : {}),
      ...(opts.i18n ? { i18n: opts.i18n } : {}),
      onSelect: (entry: ChatListEntry) => openRoom(entry),
    })
  }

  function openRoom(entry: ChatListEntry) {
    if (closed) return
    clear()
    // Drop the inbox-level subject/kind so each opened thread carries only its own.
    const { subjectId: _s, kind: _k, peerId: _p, ...base } = opts
    room = mount({
      ...base,
      inbox: false,
      launcher: false,
      el,
      ...(entry.profileId ? { profileId: entry.profileId } : {}),
      ...(entry.subjectId ? { subjectId: entry.subjectId } : {}),
      ...(entry.kind === 'direct' && entry.peerId ? { kind: 'direct', peerId: entry.peerId } : {}),
      onBack: openList,
    })
  }

  openList()
  return { close() { closed = true; clear() } }
}

export function mount(opts: MountOptions): WidgetHandle {
  // In-widget inbox: reuse the same chatlist + single-thread mount that the React
  // ChatApp composes, but in vanilla — so the HTML embed gets a real thread list
  // in place (back → list → pick → thread → back), not a host navigation.
  if (opts.inbox) return mountInbox(opts)

  // Auto-close any previous instance on this exact element before re-mounting.
  // Covers React double-invoke in StrictMode, HMR, and accidental duplicate calls.
  if (_registry.has(opts.el)) {
    _registry.get(opts.el)!.close()
    _registry.delete(opts.el)
  }
  // Launcher mode: also close any prior launcher for the same slot, even if it
  // was mounted on a now-detached div (React re-creates the ref div on
  // re-render). This is what prevents two stacked widgets after an identity
  // flicker (anonymous → logged-in).
  if (opts.launcher) _launcherRegistry.get(launcherSlot(opts))?.close()

  // Tiered identity (see MountOptions): a host-provided signed token wins, then a
  // host-vouched userId, then best-effort local persistence. The widget never
  // depends on its own storage when the host supplies identity — which is what
  // makes it safe to embed in any environment (iframes, webviews, SSR, etc.).
  // Always keep the stable per-browser anonymous id, even when the host
  // identifies the visitor — so on login we can tell the server to merge the
  // anonymous conversation into the user (Channel.io-style boot+identify).
  const anonId = persistentUid()
  let deflectTimer: ReturnType<typeof setTimeout> | undefined
  let destroyed = false
  const token = opts.token ?? opts.userId ?? anonId
  // If we're connecting as an identified user (token differs from the anon id),
  // pass the anon id as linkFrom so the server adopts any anonymous history.
  const linkFrom = token !== anonId ? anonId : undefined
  // Accept any scheme on `url` (https/http/wss/ws) and derive both the concrete
  // WebSocket URL and the REST base from it. `apiUrl` overrides the REST base
  // only when the API is on a different origin than the socket.
  const { wsUrl, httpBase } = resolveRelayUrls(opts.url, opts.apiUrl)
  let store = new ChatStore(token as never)
  /** Per-sender timers that clear a typing indicator nobody turned off. */
  const typingTimers = new Map<string, number>()
  const TYPING_TTL_MS = 30_000
  // Key the outbox by token + subjectId so each listing has its own pending queue.
  // Without this, a pending message from listing A appears as a ghost on listing B.
  const outboxKey = opts.subjectId ? `${token}::${opts.subjectId}` : token
  const outbox = new PersistentOutbox(outboxKey)
  let cid: ConversationId | undefined
  let outboxRestored = false

  let _mql: MediaQueryList | null = null
  let _mqlHandler: ((e: MediaQueryListEvent) => void) | null = null
  let _escHandler: ((e: KeyboardEvent) => void) | null = null
  let _paintTeaser: (() => void) | null = null
  // (e.g. a bare `<div id="chat"></div>` with no CSS). Without this the
  // widget's internal `height:100%` collapses to near-zero. Only applies
  // when the element truly has no height set — explicit CSS always wins.
  if (!opts.launcher && !opts.el.style.height && opts.el.clientHeight === 0) {
    opts.el.style.width = opts.el.style.width || '100%'
    opts.el.style.height = '600px'
  }

  // Restore pending outbox items for this specific listing/conversation.
  // The outbox is keyed by token+subjectId so ghost bubbles from other listings
  // never appear here.
  for (const item of outbox.load()) store.addOptimistic(item.clientMsgId, item.content)

  // ── Launcher mode ─────────────────────────────────────────────────────────
  let launcherEl: HTMLElement | null = null
  let badgeEl:    HTMLElement | null = null
  let unread = 0
  let open = !opts.launcher  // start open when not in launcher mode

  if (opts.launcher) {
    const pos = opts.position ?? 'bottom-right'
    const isRight = pos.includes('right')

    // Outer wrapper holds both the panel and the bubble button
    launcherEl = document.createElement('div')
    launcherEl.style.cssText = `position:fixed;${isRight ? 'right:20px' : 'left:20px'};bottom:20px;z-index:9999;display:flex;flex-direction:column;align-items:${isRight ? 'flex-end' : 'flex-start'};gap:12px`

    // ── Chat panel — sits above the bubble ─────────────────────────────────
    const panel = document.createElement('div')
    // The launcher builds its chrome before the room's strings are resolved
    // (that happens once the connection opens), so resolve the a11y labels here.
    const a11y = resolveStrings(opts.locale ?? opts.translateLang, opts.i18n)
    // The panel is a dialog the guest opens and closes; not modal — the page
    // behind it stays usable, and Escape closes it (see _escHandler).
    panel.setAttribute('role', 'dialog')
    panel.setAttribute('aria-label', a11y.conversation)
    // Responsive panel: full-screen on mobile (<480px); on desktop 380×600
    // CLAMPED to the viewport (`min(…)`) so a short or narrow browser window
    // never clips the composer — same clamp the React launcher already uses.
    // A MediaQueryList keeps the layout live across rotation/resize.
    const mql = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 479px)') : null
    const applyPanelLayout = (mobile: boolean) => {
      // Fullscreen is the ONLY case that strips the widget's corner radius —
      // the renderer keys .ocw-fs, never a blanket viewport query, so inline
      // mobile embeds keep their normal in-page layout.
      opts.el.classList.toggle('ocw-fs', mobile)
      panel.style.cssText = mobile ? [
        'position:fixed', 'inset:0', 'width:100%', 'height:100dvh',
        'border-radius:0', 'overflow:hidden',
        'box-shadow:none', 'display:none', 'flex-direction:column', 'background:#fff',
        'transition:opacity .18s', 'opacity:0', 'z-index:9998',
      ].join(';') : [
        'width:min(380px, calc(100vw - 40px))', 'height:min(600px, calc(100dvh - 108px))',
        'border-radius:28px', 'overflow:hidden',
        'box-shadow:0 12px 32px rgba(108,92,231,.14)',
        'display:none', 'flex-direction:column', 'background:#fff',
        'transform-origin:bottom ' + (isRight ? 'right' : 'left'),
        'transition:opacity .18s,transform .18s', 'opacity:0', 'transform:scale(.95)',
      ].join(';')
    }
    applyPanelLayout(mql?.matches ?? false)
    const mqlHandler = (e: MediaQueryListEvent): void => applyPanelLayout(e.matches)
    mql?.addEventListener('change', mqlHandler)
    _mql = mql; _mqlHandler = mqlHandler

    // Move the mount target INSIDE the panel — not full-page
    opts.el.style.cssText = 'width:100%;height:100%;overflow:hidden'
    panel.append(opts.el)

    // ── Bubble button ─────────────────────────────────────────────────────
    // Inline SVGs, not emoji: '💬'/'✕' render differently on every OS (and as
    // colour emoji can clash with the accent); these are crisp everywhere.
    const CHAT_SVG =
      '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" ' +
      'fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    const CLOSE_SVG =
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>'

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.style.cssText = [
      `width:56px;height:56px;border-radius:50%`,
      `background:${opts.accent ?? '#6c5ce7'}`,
      `color:#fff;border:none;cursor:pointer`,
      `box-shadow:0 12px 32px rgba(108,92,231,.14)`,
      `position:relative;flex:none`,
      `display:flex;align-items:center;justify-content:center`,
      `transition:transform .15s`,
    ].join(';')
    btn.onmouseenter = () => { btn.style.transform = 'scale(1.08)' }
    btn.onmouseleave = () => { btn.style.transform = 'scale(1)' }

    badgeEl = document.createElement('span')
    badgeEl.style.cssText = `position:absolute;top:-4px;right:-4px;background:#4c6fff;color:#fff;border-radius:50%;width:20px;height:20px;font-size:11px;font-weight:700;display:none;align-items:center;justify-content:center`

    // (Re)build the bubble's content for the current open state. innerHTML
    // wipes children, so the badge is re-appended each time.
    const paintBubble = () => {
      btn.innerHTML = open ? CLOSE_SVG : CHAT_SVG
      btn.setAttribute('aria-label', open ? a11y.closeChat : a11y.openChat)
      btn.setAttribute('aria-expanded', String(open))
      btn.append(badgeEl!)
    }
    paintBubble()

    launcherEl.append(panel, btn)
    document.body.append(launcherEl)

    // ── Launcher teaser (the "optional message" card) ─────────────────────
    // A small dismissible card above the button inviting a chat. Source of
    // truth: the explicit `launcherMessage` option (instant), else the
    // chatroom manifest's `defaults.launcherMessage` (arrives on connect).
    // Dismissal is per browser session so it doesn't nag across page nav.
    const teaserKey = `ocw-teaser-dismissed::${opts.profileId}`
    const optMsg = typeof opts.launcherMessage === 'string'
      ? { title: opts.launcherMessage }
      : opts.launcherMessage
    let teaserEl: HTMLElement | null = null
    const teaserDismissed = (): boolean => {
      try { return sessionStorage.getItem(teaserKey) === '1' } catch { return false }
    }
    const CHAT_MINI_SVG =
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" ' +
      'fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    const paintTeaser = (): void => {
      const msg = optMsg?.title ? optMsg : (store.launcherMessage ?? undefined)
      const shouldShow = !!msg?.title && !open && !teaserDismissed()
      if (!shouldShow) { if (teaserEl) { teaserEl.remove(); teaserEl = null } return }
      if (teaserEl) return   // already shown; don't rebuild (avoids flicker)
      teaserEl = document.createElement('div')
      teaserEl.setAttribute('role', 'button')
      teaserEl.setAttribute('tabindex', '0')
      teaserEl.setAttribute('aria-label', msg!.title)
      teaserEl.style.cssText = [
        'max-width:280px', 'background:#fff', 'border-radius:16px',
        'box-shadow:0 12px 32px rgba(108,92,231,.14)', 'padding:14px 40px 14px 16px',
        'position:relative', 'cursor:pointer', 'font-family:inherit',
        `align-self:${isRight ? 'flex-end' : 'flex-start'}`,
        'animation:ocw-teaser-in .22s ease-out',
      ].join(';')
      const title = document.createElement('div')
      title.textContent = msg!.title
      title.style.cssText = 'font-size:15px;font-weight:600;color:#111;line-height:1.35'
      teaserEl.append(title)
      if (msg!.subtitle) {
        const sub = document.createElement('div')
        sub.style.cssText = 'display:flex;align-items:center;gap:6px;margin-top:6px;font-size:13px;color:#6b7280'
        const ic = document.createElement('span'); ic.innerHTML = CHAT_MINI_SVG; ic.style.cssText = `color:${opts.accent ?? '#6c5ce7'};display:inline-flex`
        const st = document.createElement('span'); st.textContent = msg!.subtitle
        sub.append(ic, st); teaserEl.append(sub)
      }
      // Close (×) — dismiss for the session without opening the chat.
      const x = document.createElement('button')
      x.type = 'button'
      x.setAttribute('aria-label', a11y.dismiss)
      x.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>'
      x.style.cssText = 'position:absolute;top:8px;right:8px;width:24px;height:24px;border:none;border-radius:50%;background:#f3f4f6;color:#6b7280;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0'
      x.addEventListener('click', (e) => {
        e.stopPropagation()
        try { sessionStorage.setItem(teaserKey, '1') } catch { /* storage blocked — dismiss for this pageview only */ }
        if (teaserEl) { teaserEl.remove(); teaserEl = null }
      })
      teaserEl.append(x)
      const openFromTeaser = (): void => {
        if (open) return
        open = true; showPanel(true); paintBubble()
        unread = 0; if (badgeEl) badgeEl.style.display = 'none'
        if (teaserEl) { teaserEl.remove(); teaserEl = null }
      }
      teaserEl.addEventListener('click', openFromTeaser)
      teaserEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFromTeaser() } })
      // Insert ABOVE the button (button is the last child of launcherEl).
      launcherEl!.insertBefore(teaserEl, btn)
    }
    // Keyframes for the gentle pop-in (injected once).
    if (!document.getElementById('ocw-teaser-style')) {
      const st = document.createElement('style'); st.id = 'ocw-teaser-style'
      st.textContent = '@keyframes ocw-teaser-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}'
      document.head.append(st)
    }
    _paintTeaser = paintTeaser
    paintTeaser()   // shows immediately when the option is set; manifest repaints later

    const showPanel = (show: boolean) => {
      if (show) {
        panel.style.display = 'flex'
        requestAnimationFrame(() => { panel.style.opacity = '1'; panel.style.transform = 'scale(1)' })
      } else {
        panel.style.opacity = '0'; panel.style.transform = 'scale(.95)'
        setTimeout(() => { if (!open) panel.style.display = 'none' }, 180)
      }
    }

    btn.addEventListener('click', () => {
      open = !open
      showPanel(open)
      paintBubble()
      if (open) {
        unread = 0; if (badgeEl) badgeEl.style.display = 'none'
        // Opening satisfies the teaser's whole purpose — retire it for the session.
        try { sessionStorage.setItem(teaserKey, '1') } catch { /* ignore */ }
      }
      _paintTeaser?.()
    })

    // Close on Escape. Kept as a named handler so close() can REMOVE it —
    // the old anonymous listener outlived the widget (leaked on every
    // remount/identify, and a stale one could still flip `open`).
    _escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) { open = false; showPanel(false); paintBubble() }
    }
    document.addEventListener('keydown', _escHandler)
  }

  const addUnread = () => {
    if (open) return
    unread++
    if (badgeEl) { badgeEl.textContent = String(unread); badgeEl.style.display = 'flex' }
  }

  // ── Notification sound ────────────────────────────────────────────────────
  const playSound = () => {
    try {
      // ONE lazy shared context, resumed on each play. The old code created a
      // fresh AudioContext per message; browsers cap concurrent contexts (~6
      // in Chrome), after which construction throws and chat goes silent.
      const ctx = getAudioContext()
      if (!ctx) return
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {})
      const osc = ctx.createOscillator(); const gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.frequency.setValueAtTime(880, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15)
      gain.gain.setValueAtTime(0.3, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
      osc.start(); osc.stop(ctx.currentTime + 0.3)
    } catch { /* audio not available */ }
  }

  // Messages typed before the 'opened' frame arrives are queued here and
  // flushed once cid is known. This prevents silent message loss when the
  // user types immediately after the widget mounts (before WS handshake).
  const preSendQueue: { clientMsgId: string; content: import('./protocol/index.js').MessageContent }[] = []

  const flushPreSendQueue = (conversationId: ConversationId) => {
    while (preSendQueue.length) {
      const item = preSendQueue.shift()!
      outbox.add({ clientMsgId: item.clientMsgId, content: item.content, ts: Date.now() })
      conn.send({ type: 'send', conversationId, clientMsgId: item.clientMsgId, content: item.content })
    }
  }

  let conn: ConnectionManager
  // E2E is compiled out of the 'lite' build (__E2E__=false) via dead-code
  // elimination — non-encrypted chatrooms don't ship the crypto. In the lite
  // build e2e is null and encrypted rooms are refused (never sent as plaintext).
  const e2e = __E2E__ ? new E2ESession(`ocw-e2e-${opts.profileId}`) : null
  let e2eStarted = false
  // Live ECDH pending (peer not yet online)
  const pending: { clientMsgId: string; text: string }[] = []
  // X3DH async: pending send awaiting the peer's prekey bundle
  const x3dhPending: { clientMsgId: string; text: string }[] = []
  let x3dhBundleFetched = false

  const sendSealed = (clientMsgId: string, text: string): void => {
    void e2e!.sealText(text).then((content) => {
      if (cid) conn.send({ type: 'send', conversationId: cid, clientMsgId, content })
    })
  }

  const sendSealedX3DH = (clientMsgId: string, text: string, x3dhInit: { ephemeralKey: string; spkId: string; senderIK: string; usedOTP: boolean }): void => {
    void e2e!.sealText(text, x3dhInit).then((content) => {
      if (cid) conn.send({ type: 'send', conversationId: cid, clientMsgId, content })
    })
  }

  const flushPending = (): void => {
    while (pending.length) { const p = pending.shift()!; sendSealed(p.clientMsgId, p.text) }
    while (x3dhPending.length) { const p = x3dhPending.shift()!; sendSealed(p.clientMsgId, p.text) }
  }

  /** Fetch the peer's prekey bundle and perform X3DH sender init. */
  const fetchAndX3DH = (targetUserId: string): void => {
    conn.send({ type: 'fetchPrekey', targetUserId: targetUserId as never })
  }

  // Auto-localize: pick the UI language from opts.locale, else translateLang
  // (the guest's language — a host that sets translateLang but not locale still
  // gets a localized UI), else the visitor's browser language.
  const uiLocale = opts.locale ?? opts.translateLang
  const i18n = resolveStrings(uiLocale, opts.i18n)
  // RTL: Arabic (a supported UI locale) or other RTL languages (he/fa/ur) whose
  // UI strings fall back to English. Honors the resolved UI locale, not just the
  // browser, so locale:'ar' (or translateLang:'ar') flips direction.
  const rtlLocales = ['ar', 'he', 'fa', 'ur']
  const langForDir = (uiLocale ?? (typeof navigator !== 'undefined' ? navigator.language : '') ?? '').slice(0, 2).toLowerCase()
  if ((isRTL(resolveLocale(uiLocale)) || rtlLocales.includes(langForDir)) && !opts.el.dir) {
    opts.el.dir = 'rtl'
    opts.el.style.fontFamily = opts.el.style.fontFamily || 'Tahoma,Arial,system-ui,sans-serif'
  }
  // Pre-chat completion is per (chatroom, identity) — a returning visitor who
  // already qualified goes straight to the composer.
  const preChatKey = `oc_prechat_${opts.profileId}_${token.slice(-8)}`

  const renderer = new Renderer(opts.el, token, {
    onSend(text) {
      const clientMsgId = `cm_${Math.random().toString(36).slice(2)}`
      const content: import('./protocol/index.js').MessageContent = { kind: 'text', text }
      store.addOptimistic(clientMsgId, content)
      renderer.render(store)
      if (store.e2e) {
        if (!__E2E__) {
          console.error('[relay] this chat is end-to-end encrypted — use the full widget build')
          return
        }
        if (e2e!.ready) {
          sendSealed(clientMsgId, text)
        } else if (x3dhBundleFetched) {
          x3dhPending.push({ clientMsgId, text })
        } else {
          pending.push({ clientMsgId, text })
          if (store.assignedAgentId) fetchAndX3DH(store.assignedAgentId)
        }
      } else if (!cid) {
        // Connection not yet opened — queue the message; flushed on 'opened'
        preSendQueue.push({ clientMsgId, content })
      } else {
        outbox.add({ clientMsgId, content, ts: Date.now() })
        conn.send({ type: 'send', conversationId: cid, clientMsgId, content })
      }
    },
    async onAttach(file: File) {
      if (!cid) return
      const uploadUrl = `${httpBase}/upload?name=${encodeURIComponent(file.name)}`
      const clientMsgId = `cm_${Math.random().toString(36).slice(2)}`
      // Optimistic: show uploading state
      store.addOptimistic(clientMsgId, { kind: 'text', text: `📎 Uploading ${file.name}…` })
      renderer.render(store)
      try {
        const res = await fetch(uploadUrl, {
          method: 'POST',
          // Use the resolved connection token (a signed JWT, an explicit userId,
          // or the anonymous persistent uid) — NOT just opts.token. The server
          // requires an Authorization header, and the verifier accepts an
          // anonymous id as a guest; keying off opts.token alone meant anonymous
          // visitors (the common case) sent no auth and every upload 401'd.
          headers: { 'content-type': file.type, authorization: `Bearer ${token}` },
          body: file,
        })
        if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
        const { url, name, mime, size } = await res.json() as { url: string; name: string; mime: string; size: number }
        // Swap the "📎 Uploading…" placeholder for the REAL attachment content,
        // keyed by the SAME clientMsgId, BEFORE we send. Without this the
        // optimistic entry stayed as placeholder text: the server's `ack`
        // carries no content (see frames.ts), so reconciliation did
        // `{...msg, status:'sent'}` and kept the "Uploading…" text forever. The
        // real attachment only appeared on the next history fetch — i.e. after
        // a refresh — which reads exactly as "the upload is slow" even though
        // it already succeeded. Re-seeding here means the ack confirms an
        // attachment, and it renders immediately in the live view.
        store.addOptimistic(clientMsgId, { kind: 'attachment', url, name, mime, size })
        renderer.render(store)
        conn.send({ type: 'send', conversationId: cid, clientMsgId, content: { kind: 'attachment', url, name, mime, size } })
      } catch (e) {
        store.addOptimistic(clientMsgId, { kind: 'text', text: `⚠️ Upload failed: ${(e as Error).message}` })
        renderer.render(store)
      }
    },
    onInvoke(actionId, inputs) {
      if (!cid) return
      conn.send({ type: 'invoke', conversationId: cid, actionId, clientInvokeId: `iv_${Math.random().toString(36).slice(2)}`, ...(inputs ? { inputs } : {}) })
    },
    onTyping(isTyping, preview) { if (cid) conn.send({ type: 'typing', conversationId: cid, isTyping, ...(preview ? { preview } : {}) }) },
    onPreChat(values) {
      // Persist "done" per (chatroom, browser identity) so reloads skip the form.
      try { localStorage.setItem(preChatKey, '1') } catch { /* private mode */ }
      // Identity fields flow through the SAME open+userInfo path the host's
      // `user` config uses — the engine sanitizes and stores them on the
      // conversation (guestName/guestEmail; phone lands in guest meta).
      conn.send({
        type: 'open', profileId: opts.profileId as never,
        ...(opts.subjectId ? { subjectId: opts.subjectId as never } : {}),
        userInfo: {
          ...(values.name  ? { name:  values.name }  : {}),
          ...(values.email ? { email: values.email } : {}),
          ...(values.phone || values.topic ? { meta: {
            ...(values.phone ? { phone: values.phone } : {}),
            ...(values.topic ? { topic: values.topic } : {}),
          } } : {}),
        },
      } as never)
      // Topic / callback become the visible first line so agents see the
      // qualification without opening the CRM pane. A callback request is
      // explicit and carries the number.
      const first = values.callback
        ? `📞 Call-back requested${values.phone ? `: ${values.phone}` : ''}${values.topic ? ` — ${values.topic}` : ''}`
        : values.topic ? `Topic: ${values.topic}` : ''
      // E2E rooms: identity fields still flow (userInfo above), but the
      // qualification line must not be sent as plaintext into an encrypted
      // conversation — agents see topic/phone in the CRM pane instead.
      if (first && cid && !store.e2e) {
        conn.send({ type: 'send', conversationId: cid, clientMsgId: `pc_${Math.random().toString(36).slice(2, 12)}`, content: { kind: 'text', text: first } })
      }
    },
    ...(opts.features?.deflection !== false ? {
    onDeflectQuery(q: string) {
      if (destroyed) return
      // Debounced keyword lookup against the chatroom's KB — "was this your
      // question?" before the first message ever sends. Fails silent: a KB
      // hiccup must never affect typing.
      clearTimeout(deflectTimer)
      const query = q.trim()
      if (query.length < 3) { renderer.hideDeflection(); return }
      deflectTimer = setTimeout(() => {
        void fetch(`${httpBase}/kb/search?profileId=${encodeURIComponent(opts.profileId)}&q=${encodeURIComponent(query.slice(0, 200))}`)
          .then(r => (r.ok ? r.json() : { articles: [] }))
          .then((d: { articles?: { id: string; title: string; answer: string }[] }) => renderer.showDeflection(d.articles ?? []))
          .catch(() => renderer.hideDeflection())
      }, 350)
    },
    } : {}),
    onReadUpTo(seq)   { if (cid) conn.send({ type: 'read', conversationId: cid, seq }) },
    onLoadMore() {
      // WS fallback for E2E rooms where REST history can't be decrypted.
      // Non-E2E rooms use scroll-triggered REST pagination from restoreHistory().
      if (!cid || !store.e2e) return
      const oldest = store.messages()[0]
      if (oldest) conn.send({ type: 'history', conversationId: cid, beforeSeq: oldest.seq, limit: 20 })
    },
    onEdit(messageId, newText) {
      if (cid) conn.send({ type: 'edit', conversationId: cid, messageId: messageId as never, content: { kind: 'text', text: newText } })
    },
    onDelete(messageId) {
      if (cid) conn.send({ type: 'delete', conversationId: cid, messageId: messageId as never })
    },
    ...(opts.features?.reactions !== false ? {
    onReact(messageId: string, emoji: string, remove: boolean) {
      if (!cid) return
      conn.send({ type: 'react', conversationId: cid, messageId: messageId as never, emoji, remove })
    },
    } : {}),
    ...(opts.features?.csat !== false ? {
    onCsat(score: number) {
      if (!cid) return
      fetch(`${httpBase}/conversations/${cid}/csat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score }),
      }).catch(() => {})
    },
    } : {}),
    ...((() => {
      // Translation is ON BY DEFAULT: a 🌐 button under every incoming message
      // translates it into the GUEST'S OWN language. Target = explicit
      // `translateLang`, else the guest's real locale (raw navigator.language /
      // `locale` — e.g. "es", "ja", not just the en/ko UI locale), so a guest
      // whose language the UI doesn't cover still gets translations. Server
      // returns {translated:null} when no provider is configured → the button
      // degrades gracefully. Pass translateLang:'' to opt OUT.
      const rawLocale = opts.locale ?? (typeof navigator !== 'undefined' ? navigator.language : '')
      const target = opts.translateLang !== undefined ? opts.translateLang : (rawLocale ? rawLocale.split('-')[0]! : resolveLocale())
      if (!target) return {}
      return {
        async onTranslate(text: string) {
          try {
            const res = await fetch(`${httpBase}/translate`, {
              method: 'POST',
              headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
              body: JSON.stringify({ text, targetLang: target }),
            })
            if (!res.ok) return null
            const { translated } = await res.json() as { translated: string | null }
            return translated
          } catch { return null }
        },
      }
    })()),
    ...(opts.onBack ? { onBack: opts.onBack } : {}),
  }, {
    ...(opts.subject ? { subject: opts.subject } : {}),
    ...(opts.quickReplies ? { quickReplies: opts.quickReplies } : {}),
    ...(opts.accent ? { accent: opts.accent } : {}),
    ...(opts.accent2 ? { accent2: opts.accent2 } : {}),
    ...(opts.theme ? { theme: opts.theme } : {}),
    ...(opts.webfont === false ? { webfont: false } : {}),
    ...(opts.user?.name || opts.user?.avatar ? { userInfo: { ...(opts.user.name ? { name: opts.user.name } : {}), ...(opts.user.avatar ? { avatar: opts.user.avatar } : {}) } } : {}),
    i18n,

  })

  // Returning visitor who already completed pre-chat → straight to composer.
  try { if (localStorage.getItem(preChatKey)) renderer.completePreChat() } catch { /* private mode */ }

  // Identified-user display info rides on the open frame itself: the server
  // persists it onto the conversation (sanitized) so agents see who they're
  // talking to. The previous approach — sending a `note` frame after 'opened' —
  // never worked: `note` is agent-only, so the server answered FORBIDDEN and
  // the info was silently dropped. Carrying it on `open` also means it reaches
  // the dashboard for EXISTING conversations (e.g. a visitor who logs in after
  // chatting anonymously), not just brand-new empty ones.
  const userInfo = opts.user && (opts.user.name || opts.user.email || opts.user.avatar || opts.user.meta)
    ? {
        ...(opts.user.name   ? { name:   opts.user.name }   : {}),
        ...(opts.user.email  ? { email:  opts.user.email }  : {}),
        ...(opts.user.avatar ? { avatar: opts.user.avatar } : {}),
        ...(opts.user.meta   ? { meta:   opts.user.meta }   : {}),
      }
    : undefined

  const openFrame: Extract<ClientFrame, { type: 'open' }> = {
    type: 'open', profileId: opts.profileId as never,
    // Direct conversations use the kind/peerId pair; the dm:… subject key is
    // server-derived and owner-keyed, so passing it as subjectId from the
    // NON-owner side would find-or-create a junk duplicate thread.
    ...(opts.kind === 'direct' && opts.peerId
      ? { kind: 'direct' as const, peerId: opts.peerId as never }
      : opts.subjectId ? { subjectId: opts.subjectId as never } : {}),
    ...(linkFrom ? { linkFrom: linkFrom as never } : {}),
    ...(userInfo ? { userInfo } : {}),
    ...(typeof location !== 'undefined' ? { pageUrl: location.href } : {}),
    ...(typeof document !== 'undefined' && document.title ? { pageTitle: document.title } : {}),
    // Pass subject display info so the server can persist it to the Subject record.
    // This is how listingTitle and listingMeta get saved without a separate API call.
    ...(opts.subject?.title  ? { subjectTitle: opts.subject.title }             : {}),
    ...(opts.subject?.subtitle ? { subjectMeta: opts.subject.subtitle }         : {}),
  }

  conn = new ConnectionManager({
    ...(opts.refreshToken ? { refreshToken: opts.refreshToken } : {}),
    url: wsUrl, token, open: openFrame,
    getCursor: () => store.highestSeq(),
    onStatusChange: (s, msg) => renderer.setConnStatus(s, msg),
    onFrame(frame) {
      if (frame.type === 'opened') {
        cid = frame.conversation.id

        // Flush persistent outbox exactly once (idempotent: items are ack-removed).
        if (!outboxRestored) {
          outboxRestored = true
          for (const item of outbox.load()) {
            conn.send({ type: 'send', conversationId: cid, clientMsgId: item.clientMsgId, content: item.content })
          }
        }

        // Restore history on EVERY successful open (covers reconnects too).
        // On reconnect the store still has messages in memory so this is a
        // no-op if there's nothing newer — cheap REST call, correct behaviour.
        void restoreHistory(wsUrl, token, cid, store, renderer, httpBase)

        // Flush any messages typed while the connection was still opening.
        if (preSendQueue.length) flushPreSendQueue(cid)
      }

      if (frame.type === 'ack') outbox.remove(frame.clientMsgId)

      // X3DH: handle incoming prekey bundle response
      if (__E2E__ && frame.type === 'prekeyBundle') {
        if (frame.bundle) {
          x3dhBundleFetched = true
          void e2e!.x3dhSendTo(frame.bundle).then((x3dhInit) => {
            // Drain any X3DH-pending messages with the derived key.
            const toSend = [...pending.splice(0), ...x3dhPending.splice(0)]
            for (const p of toSend) sendSealedX3DH(p.clientMsgId, p.text, x3dhInit)
            renderer.render(store)
          })
        }
        // If no bundle, peer has no prekeys; fall back to live ECDH queue
        return
      }

      // X3DH recipient: detect init message in incoming encrypted messages
      // Safety net for a stuck typing indicator. The server clears the bot's
      // typing on every path, and agents send isTyping:false — but a dropped
      // socket, a crashed node or a lost frame would otherwise leave the guest
      // watching dots forever, waiting on someone who is not coming. Re-armed
      // on each typing frame, so a genuinely slow answer keeps the indicator.
      if (frame.type === 'typing' && frame.isTyping && frame.userId !== store.me) {
        clearTimeout(typingTimers.get(frame.userId))
        typingTimers.set(frame.userId, setTimeout(() => {
          store.apply({ type: 'typing', conversationId: frame.conversationId, userId: frame.userId, isTyping: false })
          renderer.render(store)
        }, TYPING_TTL_MS) as unknown as number)
      }

      if (__E2E__ && frame.type === 'message' && store.e2e) {
        const x3dh = extractX3DHInit(frame.message.content)
        if (x3dh && !e2e!.ready) {
          void e2e!.x3dhReceiveFrom(x3dh.x3dhIK, x3dh.x3dhEK, x3dh.x3dhSPK, x3dh.x3dhOTP).then(async () => {
            // Now decrypt the message that carried the init
            await e2e!.openFrame(frame)
            store.apply(frame)
            renderer.render(store)
          })
          return
        }
      }

      if (__E2E__ && frame.type === 'peerkey') {
        void e2e!.onPeerKey(frame.key).then(() => { flushPending(); renderer.render(store) })
        return
      }
      void (async () => {
        if (__E2E__ && store.e2e) await e2e!.openFrame(frame)
        store.apply(frame)
        // The manifest carries the launcher teaser (defaults.launcherMessage);
        // repaint so a manifest-configured message appears over the closed launcher.
        if (frame.type === 'manifest') {
          _paintTeaser?.()
          // A3.3 — per-chatroom bot identity: prefer the manifest's botName for
          // the bot bubble label over the generic "AI Assistant".
          const bn = (frame as { botName?: string }).botName
          if (bn) renderer.setBotName(bn)
        }
        // Trigger badge + sound for new messages from others.
        // In chatList mode, skip the badge if the user is already in the chat
        // screen for this conversation — they can see the message immediately.
        if (frame.type === 'message' && frame.message.senderId !== (token as never) && !frame.message.internal) {
          addUnread()
          playSound()
        }
        // Once we learn the room is E2E, run the key handshake exactly once.
        if (__E2E__ && store.e2e && cid && !e2eStarted) {
          e2eStarted = true
          // Upload our prekey bundle for async E2E support.
          const prekeyPayload = await e2e!.initX3DH()
          conn.send({ type: 'uploadPrekeys', ...prekeyPayload })
          // Also do live ECDH handshake in case peer is already online.
          const liveKey = await e2e!.begin()
          conn.send({ type: 'pubkey', conversationId: cid, key: liveKey })
        }
        renderer.render(store)
        // Keep seenSeq in sync so the chat list shows accurate unread counts
      })()
    },
  })


  conn.connect()
  // Show restored 'pending' bubbles (if any) immediately, before the socket opens.
  renderer.render(store)

  const slot = opts.launcher ? launcherSlot(opts) : null
  const handle: WidgetHandle = { close: () => {
    destroyed = true
    clearTimeout(deflectTimer)
    conn.close(); launcherEl?.remove(); renderer.destroy()
    if (_mql && _mqlHandler) _mql.removeEventListener('change', _mqlHandler)
    if (_escHandler) { document.removeEventListener('keydown', _escHandler); _escHandler = null }
    _registry.delete(opts.el)
    if (slot && _launcherRegistry.get(slot) === handle) _launcherRegistry.delete(slot)
  } }
  _registry.set(opts.el, handle)
  if (slot) _launcherRegistry.set(slot, handle)
  return handle
}

export { ChatStore } from './store.js'
export { ConnectionManager } from './connection.js'
export { Renderer } from './renderer.js'
export { asConversationId }
export { E2ESession, extractX3DHInit, type X3DHBundle } from './e2e.js'
export { PersistentOutbox, type OutboxItem } from './outbox.js'
export { restoreHistory, httpBaseFromWsUrl, resolveRelayUrls } from './history.js'

